import type { VirtualItem } from '@tanstack/react-virtual';
import type { IFreeSlotItem } from '../../../../../types/schedule';
import type { PlacementDayIndex } from './schedulePlacementIndex';
import { CourtPlacementHighlight } from './CourtPlacementHighlight';
import { getScheduleBodyYFromDayStartMs } from './scheduleMatchLayout';

type SchedulePlacementOverlayProps = {
  virtualColumns: VirtualItem[];
  placementDayIndex: PlacementDayIndex | undefined;
  showPlacementBands: boolean;
  headerHeight: number;
  contentHeightPx: number;
  rowHeight: number;
  scale: number;
  dayStartMs: number;
  slotMinutes: number;
  dayKey: number;
  focusedPlacementSlot?: IFreeSlotItem | null;
  courtIdsByColumnIndex: number[];
};

function isElementInScrollViewport(el: Element, scrollRoot: Element): boolean {
  const elRect = el.getBoundingClientRect();
  const rootRect = scrollRoot.getBoundingClientRect();
  return (
    elRect.left >= rootRect.left &&
    elRect.right <= rootRect.right &&
    elRect.top >= rootRect.top &&
    elRect.bottom <= rootRect.bottom
  );
}

/** Valid placement bands + keyboard focus ring above match cards (pointer-events none). */
export function SchedulePlacementOverlay({
  virtualColumns,
  placementDayIndex,
  showPlacementBands,
  headerHeight,
  contentHeightPx,
  rowHeight,
  scale,
  dayStartMs,
  slotMinutes,
  dayKey,
  focusedPlacementSlot,
  courtIdsByColumnIndex,
}: SchedulePlacementOverlayProps) {
  const columns = virtualColumns.map((virtualColumn) => {
    const courtId = courtIdsByColumnIndex[virtualColumn.index];
    if (courtId == null) return null;

    const courtIndex = placementDayIndex?.courts.find((c) => c.courtId === courtId);
    const bandsPx = showPlacementBands ? (courtIndex?.bandsPx ?? []) : [];

    const focusOnThisCourt =
      focusedPlacementSlot != null &&
      focusedPlacementSlot.day === dayKey &&
      focusedPlacementSlot.court.id === courtId;

    if (bandsPx.length === 0 && !focusOnThisCourt) return null;

    const focusTopPx = focusOnThisCourt
      ? getScheduleBodyYFromDayStartMs(
          new Date(focusedPlacementSlot!.scheduleDate).getTime(),
          dayStartMs,
          scale,
          slotMinutes
        )
      : 0;

    return (
      <div
        key={`placement-overlay-${courtId}`}
        className="schedule-placement-overlay-column"
        style={{
          transform: `translateX(${virtualColumn.start}px)`,
          width: virtualColumn.size,
          height: headerHeight + contentHeightPx,
        }}
      >
        {bandsPx.length > 0 ? (
          <CourtPlacementHighlight
            bandsPx={bandsPx}
            contentHeightPx={contentHeightPx}
            headerHeight={headerHeight}
          />
        ) : null}
        {focusOnThisCourt ? (
          <div
            className="schedule-slot-focus"
            data-schedule-slot-focus={`${focusedPlacementSlot!.day}-${focusedPlacementSlot!.court.id}-${focusedPlacementSlot!.scheduleDate}`}
            aria-hidden
            style={{
              top: headerHeight + focusTopPx,
              height: rowHeight,
              width: '100%',
            }}
          />
        ) : null}
      </div>
    );
  });

  return <>{columns}</>;
}

export function scrollPlacementSlotIntoViewIfNeeded(slot: IFreeSlotItem): void {
  requestAnimationFrame(() => {
    const el = document.querySelector(
      `[data-schedule-slot-focus="${slot.day}-${slot.court.id}-${slot.scheduleDate}"]`
    );
    if (!el) return;
    const scrollRoot = el.closest('.dayScheduleVirtual');
    if (scrollRoot && isElementInScrollViewport(el, scrollRoot)) return;
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  });
}
